// ============================================
// Shared Autofill Utilities (ES6)
// Creator와 Story-Creator 공통 유틸리티
// ============================================

import { showGlobalLoading, hideGlobalLoading, escapeHtml } from '../utils.js';
import { CURRENCY } from '../constants.js';

// ============================================
// 로딩 메시지
// ============================================

const LOADING_MESSAGES = {
  autofill: [
    '점술가의 성격을 분석하고 있어요',
    '멋진 스토리를 구상하고 있어요',
    '점술가에게 생명을 불어넣고 있어요',
    '완벽한 첫 인사를 준비하고 있어요',
    '점술가의 배경을 설정하고 있어요',
    '대화 스타일을 다듬고 있어요',
    '상황을 연출하고 있어요',
    '점술가를 꾸미고 있어요'
  ],
  image: [
    '점술가의 외모를 그리고 있어요',
    '색감을 입히고 있어요',
    '머리카락을 다듬고 있어요',
    '표정을 만들고 있어요',
    '옷을 입히고 있어요',
    '배경을 그리고 있어요',
    '조명을 조절하고 있어요',
    '마무리 터치 중이에요'
  ],
  story: [
    '스토리 구조를 분석하고 있어요',
    '점술가들의 관계를 설정하고 있어요',
    '드라마틱한 장면을 구상하고 있어요',
    '대화를 다듬고 있어요',
    '배경을 설정하고 있어요'
  ]
};

let loadingMessageInterval = null;
let currentLoadingType = 'autofill';

export function showLoadingWithMessages(type = 'autofill') {
  currentLoadingType = type;
  showGlobalLoading();
  updateLoadingText();
  loadingMessageInterval = setInterval(updateLoadingText, 3000);
}

export function hideLoadingWithMessages() {
  hideGlobalLoading();
  if (loadingMessageInterval) {
    clearInterval(loadingMessageInterval);
    loadingMessageInterval = null;
  }
}

function updateLoadingText() {
  const loadingEl = document.querySelector('.global-loading-text');
  if (loadingEl) {
    const messages = LOADING_MESSAGES[currentLoadingType] || LOADING_MESSAGES.autofill;
    const randomMsg = messages[Math.floor(Math.random() * messages.length)];
    loadingEl.textContent = randomMsg + '...';
  }
}

// ============================================
// 모델 캐러셀 렌더링
// ============================================

/**
 * 이미지 모델 캐러셀 렌더링
 * @param {string} carouselId - 캐러셀 컨테이너 ID
 * @param {Array} models - 모델 배열
 * @param {string} selectedModelId - 선택된 모델 ID
 * @param {string} modulePrefix - 모듈 접두사 (CreatorModule 또는 StoryCreatorModule)
 * @param {string} selectFnName - 선택 함수 이름
 * @param {string} modelIdKey - 모델 ID 필드명 (id 또는 modelId)
 * @param {string} imageKey - 이미지 URL 필드명 (image 또는 previewImage)
 */
export function renderModelCarousel(carouselId, models, selectedModelId, modulePrefix, selectFnName, modelIdKey = 'id', imageKey = 'image') {
  const carousel = document.getElementById(carouselId);
  if (!carousel) return;

  if (!models || models.length === 0) {
    carousel.innerHTML = '<div style="padding: 20px; color: var(--text-tertiary); font-size: 13px; text-align: center;">사용 가능한 모델이 없습니다</div>';
    return;
  }

  const effectiveSelectedId = selectedModelId || models[0]?.[modelIdKey] || '';

  carousel.innerHTML = models.map(model => {
    const modelId = model[modelIdKey];
    const imageUrl = model[imageKey] || '';
    return `
      <div class="image-model-card ${modelId === effectiveSelectedId ? 'selected' : ''}"
           data-action="selectModel" data-model-id="${modelId}">
        <div class="image-model-card-loader">
          <div class="spinner"></div>
        </div>
        <img src="${imageUrl}" alt="${escapeHtml(model.name)}" class="image-model-card-image"
             onload="this.previousElementSibling.style.display='none'; this.style.opacity='1';"
             onerror="this.previousElementSibling.style.display='none'; this.style.opacity='1';">
        <div class="image-model-card-overlay">${escapeHtml(model.name)}</div>
      </div>
    `;
  }).join('');

  // 이벤트 위임 설정
  carousel.onclick = (e) => {
    const card = e.target.closest('[data-action="selectModel"]');
    if (card) {
      const modelModule = window[modulePrefix];
      if (modelModule && typeof modelModule[selectFnName] === 'function') {
        modelModule[selectFnName](card.dataset.modelId);
      }
    }
  };

  return effectiveSelectedId;
}

/**
 * 모델 선택 처리
 * @param {string} carouselId - 캐러셀 컨테이너 ID
 * @param {string} modelId - 선택할 모델 ID
 * @param {string} hiddenInputId - hidden input ID (선택적)
 */
export function selectModelInCarousel(carouselId, modelId, hiddenInputId = null) {
  if (hiddenInputId) {
    const input = document.getElementById(hiddenInputId);
    if (input) input.value = modelId;
  }

  const carousel = document.getElementById(carouselId);
  if (carousel) {
    carousel.querySelectorAll('.image-model-card').forEach(card => {
      card.classList.remove('selected');
    });
    const selectedCard = carousel.querySelector(`.image-model-card[data-model-id="${modelId}"]`);
    if (selectedCard) selectedCard.classList.add('selected');
  }
}

// ============================================
// 태그 칩 렌더링 및 선택
// ============================================

/**
 * 태그 칩 HTML 생성
 * @param {Array} tags - 태그 배열
 * @param {string} modulePrefix - 모듈 접두사
 * @param {string} selectFnName - 선택 함수 이름
 * @returns {string} HTML 문자열
 */
export function createTagChipsHTML(tags, modulePrefix, selectFnName) {
  const sortedTags = [...tags].sort((a, b) => (a.order || 0) - (b.order || 0));

  let html = `<button type="button" class="autofill-tag-chip selected" data-genre="" data-action="selectTag">무관</button>`;

  sortedTags.forEach(tag => {
    html += `<button type="button" class="autofill-tag-chip" data-genre="${tag.id}" data-action="selectTag">${escapeHtml(tag.name || tag.id)}</button>`;
  });

  return html;
}

/**
 * 태그 칩 컨테이너에 이벤트 위임 설정
 * @param {string} containerId - 컨테이너 ID
 * @param {string} modulePrefix - 모듈 접두사
 * @param {string} selectFnName - 선택 함수 이름
 */
export function setupTagChipEvents(containerId, modulePrefix, selectFnName) {
  const container = document.getElementById(containerId);
  if (!container) return;

  container.onclick = (e) => {
    const chip = e.target.closest('[data-action="selectTag"]');
    if (chip) {
      const modelModule = window[modulePrefix];
      if (modelModule && typeof modelModule[selectFnName] === 'function') {
        modelModule[selectFnName](chip);
      }
    }
  };
}

/**
 * 태그 칩 선택 핸들러
 * @param {string} containerId - 칩 컨테이너 ID
 * @param {HTMLElement} chip - 클릭된 칩 요소
 * @param {Array} currentSelections - 현재 선택된 태그 배열
 * @param {Function} setSelections - 선택 배열 업데이트 함수
 * @returns {Array} 업데이트된 선택 배열
 */
export function handleTagChipSelection(containerId, chip, currentSelections, setSelections) {
  const genre = chip.dataset.genre;
  const container = document.getElementById(containerId);
  if (!container) return currentSelections;

  const chips = container.querySelectorAll('.autofill-tag-chip');
  const noGenreChip = container.querySelector('.autofill-tag-chip[data-genre=""]');

  if (genre === '') {
    // "무관" 선택 시 모든 선택 해제
    setSelections([]);
    chips.forEach(c => c.classList.toggle('selected', c.dataset.genre === ''));
    return [];
  } else {
    // 특정 태그 선택
    if (noGenreChip) noGenreChip.classList.remove('selected');

    const selections = [...currentSelections];
    const idx = selections.indexOf(genre);

    if (idx === -1) {
      selections.push(genre);
      chip.classList.add('selected');
    } else {
      selections.splice(idx, 1);
      chip.classList.remove('selected');
    }

    // 선택된 것이 없으면 "무관" 활성화
    if (selections.length === 0 && noGenreChip) {
      noGenreChip.classList.add('selected');
    }

    setSelections(selections);
    return selections;
  }
}

// ============================================
// 토글 함수
// ============================================

/**
 * 이미지 옵션 섹션 토글 (CSS class 방식)
 * @param {string} checkboxId - 체크박스 ID
 * @param {string} sectionId - 섹션 ID
 */
export function toggleImageOptionSection(checkboxId, sectionId) {
  const checkbox = document.getElementById(checkboxId);
  const section = document.getElementById(sectionId);
  if (checkbox && section) {
    if (checkbox.checked) {
      section.classList.remove('disabled');
    } else {
      section.classList.add('disabled');
    }
  }
}

/**
 * 일반 섹션 토글 (display 방식)
 * @param {string} checkboxId - 체크박스 ID
 * @param {string} sectionId - 섹션 ID
 */
export function toggleDisplaySection(checkboxId, sectionId) {
  const checkbox = document.getElementById(checkboxId);
  const section = document.getElementById(sectionId);
  if (checkbox && section) {
    section.style.display = checkbox.checked ? 'block' : 'none';
  }
}

// ============================================
// 비용 계산
// ============================================

/**
 * 자동 채우기 비용 계산
 * @param {number} baseCost - 기본 비용
 * @param {boolean} needsImage - 이미지 생성 필요 여부
 * @param {string} selectedModelId - 선택된 모델 ID
 * @param {Array} models - 모델 배열
 * @param {string} modelIdKey - 모델 ID 필드명
 * @param {number} multiplier - 이미지 비용 배수 (캐릭터 수 등)
 * @returns {number} 총 비용
 */
export function calculateAutofillCost(baseCost, needsImage, selectedModelId, models, modelIdKey = 'id', multiplier = 1) {
  if (!needsImage || !selectedModelId) {
    return baseCost;
  }

  const model = models.find(m => m[modelIdKey] === selectedModelId);
  const imageCost = model?.carbonCost || 3;
  return baseCost + (imageCost * multiplier);
}

/**
 * 비용 표시 업데이트
 * @param {string} costElementId - 비용 표시 요소 ID
 * @param {number} cost - 비용
 */
export function updateCostDisplay(costElementId, cost) {
  const costEl = document.getElementById(costElementId);
  if (costEl) {
    costEl.textContent = `${cost} ${CURRENCY.UNIT}`;
  }
}

// ============================================
// 탭 전환
// ============================================

/**
 * 자동채우기 탭 전환
 * @param {string} modalSelector - 모달 셀렉터 (예: '#autofillConfirmModal')
 * @param {string} tabId - 탭 ID ('fill' 또는 'generate')
 * @param {string} fillTabId - 채우기 탭 콘텐츠 ID
 * @param {string} generateTabId - 생성 탭 콘텐츠 ID
 */
export function switchAutofillTabGeneric(modalSelector, tabId, fillTabId, generateTabId) {
  // 탭 버튼 활성화
  document.querySelectorAll(`${modalSelector} .story-autofill-tab`).forEach(tab => {
    tab.classList.toggle('active', tab.dataset.tab === tabId);
  });

  // 탭 콘텐츠 활성화
  const fillTab = document.getElementById(fillTabId);
  const generateTab = document.getElementById(generateTabId);

  if (fillTab) fillTab.classList.toggle('active', tabId === 'fill');
  if (generateTab) generateTab.classList.toggle('active', tabId === 'generate');
}

// ============================================
// 모달 관리
// ============================================

/**
 * 모달 열기 (fade-in)
 * @param {string} modalId - 모달 ID
 */
export function openAutofillModalGeneric(modalId) {
  const modal = document.getElementById(modalId);
  if (!modal) return false;

  modal.style.display = 'flex';
  requestAnimationFrame(() => {
    modal.classList.add('active');
  });

  if (window.ModalManager) {
    window.ModalManager.push(modalId);
  }

  return true;
}

/**
 * 모달 닫기 (fade-out)
 * @param {string} modalId - 모달 ID
 */
export function closeAutofillModalGeneric(modalId) {
  const modal = document.getElementById(modalId);
  if (!modal) return;

  modal.classList.remove('active');
  setTimeout(() => {
    if (!modal.classList.contains('active')) {
      modal.style.display = 'none';
    }
  }, 300);

  if (window.ModalManager) {
    window.ModalManager.remove(modalId, true);
  }
}

// ============================================
// 태그 데이터 로드 (Firestore)
// ============================================

let cachedTags = null;

/**
 * Firestore에서 자동채우기 태그 로드
 * @returns {Promise<Object>} 태그 데이터
 */
export async function loadAutofillTagsFromFirestore() {
  if (cachedTags) return cachedTags;

  try {
    const db = firebase.firestore();
    const doc = await db.collection('settings').doc('autofillTags').get();

    if (doc.exists) {
      const data = doc.data();
      if (data.tags) {
        cachedTags = { tags: data.tags };
      } else {
        const merged = [...(data.situationTags || []), ...(data.subGenreTags || [])];
        cachedTags = { tags: merged };
      }
      return cachedTags;
    }
  } catch (err) {
    console.warn('태그 로드 실패:', err);
  }

  // 기본 태그
  return {
    tags: [
      { id: '학원', name: '학원' }, { id: '캠퍼스', name: '캠퍼스' }, { id: '직장', name: '직장' },
      { id: '힐링', name: '힐링' }, { id: '로판', name: '로판' }, { id: '피폐', name: '피폐' },
      { id: '재회', name: '재회' }, { id: '판타지', name: '판타지' }, { id: '갈등', name: '갈등' }
    ]
  };
}

/**
 * 태그 캐시 초기화
 */
export function clearTagsCache() {
  cachedTags = null;
}
